import { useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import React, { useCallback, useState } from 'react';
import getTopicPrivacyError, {
  getParentPrivacyError,
  getSubtopicPrivacyError,
  isLimitedByPlan,
} from './getTopicPrivacyError';
import * as styles from './TopicModal.module.scss';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Button from '../../components/Button';
import CallOut from '../../components/CallOut';
import Form, { Field, FieldSet } from '../../components/Form';
import FormikSubmitButton from '../../components/formInternals/FormikSubmitButton';
import Modal, { ModalActionFooter, ModalTitle } from '../../components/Modal';
import SelectOption from '../../components/SelectOption';
import SelectValue from '../../components/SelectValue';
import Tooltip from '../../components/Tooltip';
import { BEACON_TOPIC_EDIT_MODAL } from '../../constants/onboarding';
import { gql } from '../../graphql/generated/gql';
import type { PlanTier } from '../../graphql/generated/graphql';
import {
  TopicDetached,
  TopicMemberEditable,
  TopicPrivacy,
} from '../../graphql/generated/graphql';
import topicsIcon from '../../icons/non-prefilled/16x16/allTopics.svg';
import postIcon from '../../icons/non-prefilled/16x16/post.svg';
import postWithContentIcon from '../../icons/non-prefilled/16x16/postWithContent.svg';
import provisionsIcon from '../../icons/non-prefilled/16x16/provisions.svg';
import readIcon from '../../icons/non-prefilled/16x16/read.svg';
import removeIcon from '../../icons/non-prefilled/16x16/remove.svg';
import TutorialBeacon from '../../onboarding/tutorial/TutorialBeacon';
import UpgradeBadge from '../../organization/UpgradeBadge';
import { isFeatureEnabled } from '../../utils/features';
import Link from '../../utils/router/Link';
import { openPricingModal } from '../../utils/url/pricingPath';
import { usePublicContentPermissions } from '../../utils/useAllowPublicContentBy';
import getTopicPrivacyInfo, { getPrivacyLabel } from '../getTopicPrivacyInfo';
import topicPolicyCheck, {
  isOwner,
  TopicPolicyAction,
} from '../topicPolicyCheck';
import TopicSelectValue from '../TopicSelectValue';
import type { AllTopicsTreeListTopic } from '../useAllTopics';
import { useAllTopicMap } from '../useAllTopics';
import useTopicHierarchicalSelectProps from '../useTopicHierarchicalSelectProps';

const ALLOW_USER_ROOT_TOPIC_QUERY = gql(/** @complexity 3 */ `
  query AllowUserRootTopic {
    session: currentSession {
      organization {
        id
        settings {
          allowUserRootTopic
        }
      }
    }
  }
`);

function useTopLevelTopicPermissions() {
  const isAdmin = useAppSelector(
    (state) => state.session.user.type === 'ADMIN',
  );
  const { data } = useQuery(ALLOW_USER_ROOT_TOPIC_QUERY);

  const isRootTopicAllowed =
    isAdmin || data?.session.organization.settings?.allowUserRootTopic;
  return { isRootTopicAllowed };
}

const LOSE_SUBTOPIC_ACCESS =
  'You may lose access to subtopics you are not a member of.';

const getTopicId = (topic: { id: string }) => topic.id;

const mayLoseSubtopicAccess = (privacy: TopicPrivacy) =>
  [TopicPrivacy.Private, TopicPrivacy.Secret].includes(privacy);

const privacyMustPropagate = (privacy: TopicPrivacy) =>
  [TopicPrivacy.Public, TopicPrivacy.Private, TopicPrivacy.Secret].includes(
    privacy,
  );

const getPrivacyOptions = ({
  parentPrivacy,
  planTier,
  isPermittedToSetTopicToPublic,
}: {
  parentPrivacy: TopicPrivacy;
  planTier?: PlanTier;
  isPermittedToSetTopicToPublic: boolean;
}) =>
  [
    TopicPrivacy.Open,
    TopicPrivacy.Public,
    TopicPrivacy.Private,
    TopicPrivacy.Secret,
  ].map((privacy) => {
    const error = getTopicPrivacyError(privacy, {
      parentPrivacy,
      planTier,
      isPermittedToSetTopicToPublic,
    });
    const privacyInfo = getTopicPrivacyInfo(privacy);

    return {
      ...privacyInfo,
      helpText: privacyInfo.helpText.replace(/\n/g, ' '),
      value: privacy,
      isDisabled: !!error,
      tooltip: error,
    };
  });

type ParentId = string | null | undefined;

interface FormValues {
  propagatePrivacy: boolean;
  parentId: ParentId;
  privacy: TopicPrivacy;
  name: string;
  memberEditable?: TopicMemberEditable;
  detached?: TopicDetached;
}

interface TopicModalProps {
  close(): void;
  privacy: TopicPrivacy;
  submitText: string;
  title: string;
  onSubmit(values: FormValues): Promise<void>;
  id?: string;
  name?: string;
  memberEditable?: TopicMemberEditable;
  detached?: TopicDetached;
  parentId?: string;
  onDelete?: () => void;
}

const TopicModal = ({
  close,
  id,
  onDelete,
  onSubmit,
  submitText,
  title,
  ...initialValues
}: TopicModalProps) => {
  const dispatch = useAppDispatch();
  const [currentParentId, setCurrentParentId] = useState<ParentId>(
    initialValues.parentId,
  );
  const currentUser = useAppSelector((state) => state.session.user);
  const planTier =
    useAppSelector((state) => state.session.organization.planTier) ?? undefined;
  const { isPermittedToSetTopicToPublic } = usePublicContentPermissions();
  const { isRootTopicAllowed } = useTopLevelTopicPermissions();
  const topicMap = useAllTopicMap();
  const topic = id ? topicMap[id] : null;

  const getParentError = useCallback(
    (parentTopic: { privacy: TopicPrivacy }) =>
      getParentPrivacyError(parentTopic.privacy, {
        planTier,
        isPermittedToSetTopicToPublic,
      }),
    [isPermittedToSetTopicToPublic, planTier],
  );

  const isParentDisabled = useCallback(
    (parentTopic: AllTopicsTreeListTopic) =>
      parentTopic.id === id ||
      !topicPolicyCheck(
        TopicPolicyAction.AddSubtopic,
        parentTopic,
        currentUser,
      ) ||
      (!!id &&
        !!parentTopic.ancestors.find((ancestor) => ancestor.id === id)) ||
      !!getParentError(parentTopic),
    [currentUser, getParentError, id],
  );

  const parentSelectProps = useTopicHierarchicalSelectProps({
    isDisabled: isParentDisabled,
    tooltip: getParentError,
    currentValue: currentParentId,
    getValue: getTopicId,
  });

  const privacyForTopicId = (topicId: ParentId) => {
    const topicFromMap = topicId ? topicMap[topicId] : null;
    return topicFromMap ? topicFromMap.privacy : TopicPrivacy.Open;
  };

  const topicPrivacyMustPropagate = (topicId: ParentId) =>
    privacyMustPropagate(privacyForTopicId(topicId));

  const shouldDisableGuestDelete =
    topic != null &&
    !topicPolicyCheck(TopicPolicyAction.DeleteTopic, topic, currentUser);

  const hasSubtopics = (topic?.childTopics?.length ?? 0) > 0;

  // Guests cannot create root level topics
  const initialParentId: string | undefined =
    initialValues.parentId ||
    (currentUser.type === 'GUEST' &&
    parentSelectProps.options.length > 0 &&
    id == null
      ? parentSelectProps.options[0]?.value
      : undefined);

  const getPrivacy = (privacy: TopicPrivacy, parentId: ParentId) => {
    const parentPrivacy = privacyForTopicId(parentId);
    return getSubtopicPrivacyError(privacy, parentPrivacy)
      ? parentPrivacy
      : privacy;
  };

  const getValuesForPrivacy = (privacy: TopicPrivacy, parentId: ParentId) => ({
    privacy: getPrivacy(privacy, parentId),
    propagatePrivacy: privacyMustPropagate(privacy),
  });

  const initialPrivacy = getPrivacy(
    initialValues.privacy,
    initialValues.parentId,
  );

  const validate = (values: FormValues) => {
    const errors: { parentId?: string; privacy?: string } = {};
    const parentPrivacy = privacyForTopicId(values.parentId);
    const parentPrivacyError = getParentPrivacyError(parentPrivacy, {
      planTier,
      isPermittedToSetTopicToPublic,
    });

    if (parentPrivacyError) {
      errors.parentId = parentPrivacyError;
    }

    const topicPrivacyError = getTopicPrivacyError(values.privacy, {
      parentPrivacy,
      planTier,
      isPermittedToSetTopicToPublic,
    });

    if (topicPrivacyError) {
      errors.privacy = topicPrivacyError;
    }

    return errors;
  };

  const formInitialValues: FormValues = {
    name: '',
    ...initialValues,
    propagatePrivacy: privacyMustPropagate(initialPrivacy),
    parentId: initialParentId,
    privacy: initialPrivacy,
  };
  const initialErrors = validate(formInitialValues);
  const initialTouched = _.mapValues(initialErrors, (error) => !!error);

  return (
    <Modal>
      <Form<FormValues>
        initialValues={formInitialValues}
        onSubmit={onSubmit}
        validateOnBlur={false}
        validate={validate}
        initialErrors={initialErrors}
        initialTouched={initialTouched}
        enableReinitialize
      >
        {({ values, setValues, isValid }) => {
          const { memberEditable, parentId, privacy } = values;
          const isDisabledOnFreeTier = isLimitedByPlan(privacy, planTier);

          return (
            <div className={styles.container}>
              <ModalTitle close={close}>{title}</ModalTitle>
              {isDisabledOnFreeTier && (
                <div className={styles.callOut}>
                  <CallOut variant="danger">
                    <strong>
                      {getPrivacyLabel(privacy)} topics are not available on
                      your plan.
                    </strong>{' '}
                    To edit this topic, either change the access or{' '}
                    {currentUser.type === 'ADMIN' ? (
                      <Link to="/admin/billing" onClick={close}>
                        upgrade your plan
                      </Link>
                    ) : (
                      'upgrade your plan'
                    )}
                    .
                  </CallOut>
                </div>
              )}
              <Field
                autoFocus
                label="Name"
                name="name"
                required
                type="text"
                autoComplete="off"
                disabled={isDisabledOnFreeTier}
              />
              <Field
                {...{
                  ...parentSelectProps,
                  components: {
                    ...parentSelectProps.components,
                    SingleValue: TopicSelectValue,
                  },
                }}
                isClearable={currentUser.type !== 'GUEST'}
                label={
                  <TutorialBeacon id={BEACON_TOPIC_EDIT_MODAL} placement="left">
                    {(beaconProps) => (
                      <span {...beaconProps}>Organize under…</span>
                    )}
                  </TutorialBeacon>
                }
                required={
                  !isRootTopicAllowed &&
                  (topic == null || topic?.parent != null)
                }
                name="parentId"
                placeholder="Choose location of topic"
                type="select"
                // @ts-expect-error
                onChange={(newParentId: string | null) => {
                  const nextParentId =
                    newParentId == null && topic?.parent == null
                      ? undefined
                      : newParentId;

                  setCurrentParentId(nextParentId);
                  if (topicPrivacyMustPropagate(nextParentId)) {
                    setValues({
                      ...values,
                      parentId: nextParentId,
                      ...getValuesForPrivacy(values.privacy, nextParentId),
                    });
                  } else {
                    setValues({
                      ...values,
                      parentId: nextParentId,
                    });
                  }
                }}
              />
              {isRootTopicAllowed && currentParentId == null && (
                <div className={styles.callOut}>
                  <CallOut variant="info">
                    Topics with no parent location will be organized as top
                    level.
                  </CallOut>
                </div>
              )}
              {topicPrivacyMustPropagate(parentId) && (
                <div className={styles.callOut}>
                  <CallOut variant="warning">
                    Organizing under a{' '}
                    {getPrivacyLabel(privacyForTopicId(parentId))} topic will
                    make this topic {hasSubtopics ? 'and all subtopics ' : ''}
                    {getPrivacyLabel(privacyForTopicId(parentId))} as well.
                    {hasSubtopics &&
                      mayLoseSubtopicAccess(privacyForTopicId(parentId)) &&
                      ` ${LOSE_SUBTOPIC_ACCESS}`}
                  </CallOut>
                </div>
              )}
              <FieldSet
                legend={
                  <>
                    Who can access…
                    {planTier === 'FREE' && (
                      <span className={styles.upgradeBadgeContainer}>
                        <Tooltip content={'Upgrade to change\ntopic privacy'}>
                          <UpgradeBadge
                            onClick={() => {
                              openPricingModal(dispatch, 'advancedTopics');
                            }}
                          />
                        </Tooltip>
                      </span>
                    )}
                  </>
                }
                margin="none"
              >
                <Field
                  className={styles.permissionSelect}
                  components={{
                    Option: SelectOption<TopicPrivacy>,
                    SingleValue: SelectValue<TopicPrivacy>,
                  }}
                  name="privacy"
                  options={getPrivacyOptions({
                    parentPrivacy: privacyForTopicId(parentId),
                    planTier,
                    isPermittedToSetTopicToPublic,
                  })}
                  onChange={(newPrivacy) =>
                    setValues({
                      ...values,
                      ...getValuesForPrivacy(newPrivacy, values.parentId),
                    })
                  }
                  type="select"
                  isSearchable={false}
                  margin={!hasSubtopics ? 'normal' : 'compact'}
                />
              </FieldSet>
              {hasSubtopics && (
                <>
                  <Field
                    type="checkbox"
                    label={
                      {
                        [TopicPrivacy.Open]:
                          'Also set all editable subtopics to Open',
                        [TopicPrivacy.Private]:
                          'Also set all subtopics to Private',
                        [TopicPrivacy.Public]:
                          'Also set all subtopics to Public',
                        [TopicPrivacy.Secret]:
                          'Also set all subtopics to Secret',
                      }[privacy]
                    }
                    name="propagatePrivacy"
                    margin="normal"
                    disabled={privacyMustPropagate(privacy)}
                  />
                  {!topicPrivacyMustPropagate(parentId) &&
                    privacyMustPropagate(privacy) && (
                      <div className={styles.callOut}>
                        <CallOut variant="warning">
                          {getPrivacyLabel(privacy)} topics can only have{' '}
                          {privacy === TopicPrivacy.Private
                            ? `${getPrivacyLabel(
                                TopicPrivacy.Private,
                              )} or ${getPrivacyLabel(TopicPrivacy.Secret)}`
                            : getPrivacyLabel(privacy)}{' '}
                          subtopics.
                          {mayLoseSubtopicAccess(privacy) &&
                            ` ${LOSE_SUBTOPIC_ACCESS}`}
                        </CallOut>
                      </div>
                    )}
                </>
              )}
              {isFeatureEnabled('detached_topics') && (
                <Field
                  className={styles.permissionSelect}
                  components={{
                    Option: SelectOption<TopicDetached>,
                    SingleValue: SelectValue<TopicDetached>,
                  }}
                  label="Where to list…"
                  name="detached"
                  options={[
                    {
                      value: TopicDetached.None,
                      label: 'Discoverable',
                      icon: postWithContentIcon,
                      helpText:
                        'Include in sidebar and when browsing parent topic pages',
                    },
                    {
                      value: TopicDetached.Sidebar,
                      label: 'Topics',
                      icon: topicsIcon,
                      helpText:
                        'Only include in parent topic pages. Omit listing in the sidebar',
                    },
                    {
                      value: TopicDetached.All,
                      label: 'None',
                      icon: removeIcon,
                      helpText: 'Omit from listing in sidebar and topics pages',
                    },
                  ]}
                  type="select"
                  isSearchable={false}
                />
              )}
              <Field
                className={styles.permissionSelect}
                components={{
                  Option: SelectOption,
                  SingleValue: SelectValue,
                }}
                label="Members can edit…"
                name="memberEditable"
                options={[
                  {
                    value: TopicMemberEditable.All,
                    label: 'All',
                    icon: provisionsIcon,
                    helpText:
                      privacy === 'PRIVATE'
                        ? 'All topic members can edit this topic and its posts.'
                        : 'All topic members can edit this topic. Anyone can edit its posts.',
                  },
                  {
                    value: TopicMemberEditable.Post,
                    label: 'Posts',
                    icon: postIcon,
                    helpText:
                      privacy === 'PRIVATE'
                        ? 'Only topic owners can edit this topic. Members can only edit its posts.'
                        : 'Only topic owners can edit this topic. Teammates can only edit posts.',
                  },
                  {
                    value: TopicMemberEditable.None,
                    label: 'None',
                    icon: readIcon,
                    helpText:
                      privacy === 'PRIVATE'
                        ? 'Only topic owners can edit this topic and its posts. Members can only view.'
                        : 'Only topic owners can edit this topic and its posts. Teammates can only view.',
                  },
                ]}
                type="select"
                isSearchable={false}
                disabled={isDisabledOnFreeTier}
              />
              {id != null &&
                memberEditable !== 'ALL' &&
                topic != null &&
                !isOwner(topic, currentUser) && (
                  <div className={styles.callOut}>
                    <CallOut variant="danger">
                      Since you are not an owner, you will be unable to edit
                      this topic{' '}
                      {memberEditable === 'NONE' ? 'or its posts ' : ''}
                      after saving.
                    </CallOut>
                  </div>
                )}
              <ModalActionFooter
                secondary={
                  onDelete && (
                    <Tooltip
                      content={
                        shouldDisableGuestDelete
                          ? 'Guests cannot delete\ntop level topics'
                          : undefined
                      }
                    >
                      <Button
                        disabled={shouldDisableGuestDelete}
                        danger
                        onClick={onDelete}
                      >
                        Delete topic
                      </Button>
                    </Tooltip>
                  )
                }
                primary={
                  <FormikSubmitButton size="large" disabled={!isValid}>
                    {submitText}
                  </FormikSubmitButton>
                }
              />
            </div>
          );
        }}
      </Form>
    </Modal>
  );
};

export default TopicModal;
